<template>
  <Transition name="slide-up">
    <div
      v-if="isOpen"
      ref="panelRef"
      class="mobile-section-panel no-print"
      role="dialog"
      :aria-label="title"
      aria-modal="true"
      tabindex="0"
      :style="{ '--mobile-panel-height': panelMaxHeight }"
      @change="handleChange"
    >
      <div class="mobile-section-panel-header">
        <div class="mobile-section-panel-title-group">
          <span class="mobile-section-panel-title">
            {{ title }}
          </span>
          <span
            v-if="badge"
            class="tag is-white has-text-info"
            style="font-weight: 600; height: 2rem; font-size: 1rem"
            >{{ badge }}</span
          >
          <div class="mobile-section-panel-actions">
            <slot name="header-actions"></slot>
          </div>
        </div>
        <button
          type="button"
          class="mobile-section-panel-close"
          aria-label="Close panel"
          @click="emit('close')"
        >
          <span class="icon">
            <i class="fas fa-times" aria-hidden="true"></i>
          </span>
        </button>
      </div>
      <div
        class="mobile-section-panel-content"
        :class="{ 'has-extra-bottom-padding': sectionKey === 'other-ingredients' }"
        data-scroll-hint-vertical
        data-scroll-hint-vertical-offset-bottom="0"
      >
        <slot />
      </div>
    </div>
  </Transition>
</template>

<script setup>
  import { computed, nextTick, watch, ref, onMounted, onBeforeUnmount, inject } from "vue";

  const props = defineProps({
    isOpen: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    heightBasis: {
      type: Number,
      default: 50,
    },
    sectionKey: {
      type: String,
      default: null,
    },
    badge: {
      type: [String, Number],
      default: null,
    },
  });

  const emit = defineEmits(["close"]);
  const panelRef = ref(null);
  const panelMaxHeight = computed(() => `calc(${props.heightBasis}vh - 60px)`);

  const triggerSave = inject("triggerSave");

  function handleChange(event) {
    if (triggerSave) {
      triggerSave();
    }
  }

  watch(
    () => props.isOpen,
    (open) => {
      if (open) {
        nextTick(() => {
          document.dispatchEvent(new Event("scroll-hint:scan"));
          panelRef.value?.focus();
        });
      }
    }
  );

  function handleFocusTrap(e) {
    if (!props.isOpen) return;
    if (e.key !== "Tab") return;
    const panel = panelRef.value;
    if (!panel) return;
    const focusable = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  onMounted(() => {
    document.addEventListener("keydown", handleFocusTrap);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", handleFocusTrap);
  });
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .mobile-section-panel {
    position: fixed !important;
    bottom: var(--mobile-bottom-bar-height, 60px) !important;
    left: 0 !important;
    right: 0 !important;
    background: $white !important;
    border-top: 1px solid $grey-lighter;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    z-index: 9998 !important;

    // Mobile: full height
    @include until($tablet) {
      top: 0 !important;
      max-height: none;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    // Tablet: keep per-section max-height rules (min 65vh for better content visibility)
    @include from($tablet) {
      max-height: var(--mobile-panel-height, calc(65vh - 60px));
    }

    @include from($desktop) {
      display: none !important;
    }
  }

  .mobile-section-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    flex-shrink: 0;
    background-color: $info;
  }

  .mobile-section-panel-title-group {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .mobile-section-panel-title {
    font-weight: 600;
    font-size: 1rem;
    color: $white;
  }

  .mobile-section-panel-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .mobile-section-panel-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: $white;
    padding: 0.5rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;

    &:hover {
      color: $white;
      background-color: rgba($white, 0.2);
    }

    .icon {
      font-size: 1.25rem;
    }
  }

  .mobile-section-panel-content {
    flex: 1;
    overflow-y: scroll;
    padding: 0.5rem 1rem 0;
    -webkit-overflow-scrolling: touch;
    position: relative;

    &.has-extra-bottom-padding {
      padding-bottom: 1rem;
    }

    :deep(.panel) {
      border: none;
      box-shadow: none;
      margin: 0;

      &.mb-3 {
        margin-bottom: 0 !important;
      }

      .collapsible-panel-block {
        padding: 0 !important;
      }
    }

    @include until($desktop) {
      :deep(.panel-heading) {
        display: none;
      }

      // Force panels to always show as open since we hide the toggle
      :deep(.panel.is-collapsed .is-block) {
        display: block !important;
      }

      // Hide dropdown icons on mobile
      :deep(.control.has-icons-left .icon.is-left) {
        display: none;
      }

      :deep(.select:not(.is-multiple):not(.is-loading)::after) {
        display: none;
      }
    }
  }

  .slide-up-enter-active,
  .slide-up-leave-active {
    transition:
      transform 0.25s ease,
      opacity 0.25s ease;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
  }

  .slide-up-enter-to,
  .slide-up-leave-from {
    transform: translateY(0);
    opacity: 1;
  }
</style>
